import React from 'react';
import type { ComponentProps } from 'react';
import type { Nullable, SignupFlow, StringMap } from 'corgi-types';
import type { UserType } from 'graphql-ts-types';

import AutoScrollAlert from 'common/components/AutoScrollAlert';
import { Flex } from 'common/components/Flex';
import type { Slices } from 'common/components/HomeClubDrawer/types';
import SeparatorWithText from 'common/components/SeparatorWithText';
import StyledContainerWithBackground from 'common/components/StyledContainerWithBackground';
import { BodyRegularBR2, HeadlineH5 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import LoginBackToLink from '../../login-back-to-link/components/LoginBackToLink';
import ThirdPartyLoginSection from '../../login-form/components/ThirdParty/ThirdPartyLoginSection';
import type { ThirdPartySignUpData } from '../types';
import Form from './Form';
import ThirdPartyDataHeading from './Form/ThirdPartyDataHeading';
import type { FormData as RegistrationFormData } from './Form/utils';

const headingStyles: ComponentProps<typeof HeadlineH5> = {
  as: 'h1',
  mt: 'sp_0',
  mb: 'sp_16',
};

export interface RegistrationFormContentProps {
  commonError: { message: string; timestamp: number } | null;
  userFlow: SignupFlow;
  appleClientId: string;
  appleStateToken: string;
  handleRegistrationSuccess: (args?: {
    queryParams?: StringMap;
    formData?: RegistrationFormData;
  }) => void;
  setCommonError: (errorMessage: Nullable<string>) => void;
  withAppleFirst: boolean;
  onFailedHomeClubOptIn: () => void;
  showHomeClubConsent: boolean;
  homeClubSlices: Nullable<Slices>;
  setIsDisabled: (disabled: boolean) => void;
  setUserType: (userType: UserType) => void;
  isDisabled: boolean;
  userType: UserType;
  formUrls: {
    loginUrl: string;
    forgotPasswordUrl: string;
  };
  referredBy?: string;
  isThirdPartySignUpFlow?: boolean;
  thirdPartySignUpData?: ThirdPartySignUpData;
}

const RegistrationFormContent = ({
  commonError,
  isThirdPartySignUpFlow,
  thirdPartySignUpData,
  userFlow,
  appleClientId,
  appleStateToken,
  handleRegistrationSuccess,
  setCommonError,
  referredBy,
  withAppleFirst,
  onFailedHomeClubOptIn,
  showHomeClubConsent,
  homeClubSlices,
  setIsDisabled,
  setUserType,
  isDisabled,
  userType,
  formUrls,
}: RegistrationFormContentProps) => {
  const { t } = useLocalization();

  return (
    <>
      {commonError && (
        <AutoScrollAlert message={commonError.message} messageId={commonError.timestamp} />
      )}

      <section aria-labelledby="registration-form-heading">
        <StyledContainerWithBackground>
          <HeadlineH5 {...headingStyles} id="registration-form-heading">
            {t('h24_registration_form_heading')}
          </HeadlineH5>

          {isThirdPartySignUpFlow ? (
            <ThirdPartyDataHeading provider={thirdPartySignUpData.provider} />
          ) : (
            <>
              <ThirdPartyLoginSection
                userFlow={userFlow}
                appleClientId={appleClientId}
                appleStateToken={appleStateToken}
                setCommonError={setCommonError}
                onSuccess={handleRegistrationSuccess}
                referredBy={referredBy}
                withSeparator={false}
                withAppleFirst={withAppleFirst}
              />

              <SeparatorWithText mt="sp_16" mb="sp_16">
                <BodyRegularBR2>{t('h24_continue_with_email')}</BodyRegularBR2>
              </SeparatorWithText>
            </>
          )}

          <Form
            isThirdPartySignUpFlow={isThirdPartySignUpFlow}
            onFailedHomeClubOptIn={onFailedHomeClubOptIn}
            thirdPartySignUpData={thirdPartySignUpData}
            showHomeClubConsent={showHomeClubConsent}
            homeClubSlices={homeClubSlices}
            setCommonError={setCommonError}
            onSuccess={handleRegistrationSuccess}
            setIsDisabled={setIsDisabled}
            setUserType={setUserType}
            referredBy={referredBy}
            isDisabled={isDisabled}
            userFlow={userFlow}
            userType={userType}
            {...formUrls}
          />

          <Flex justifyContent="center">
            <LoginBackToLink userFlow={userFlow} />
          </Flex>
        </StyledContainerWithBackground>
      </section>
    </>
  );
};

export default RegistrationFormContent;
